import moment from 'moment';
import {
	Flag,
	Repeat,
	AlertTriangle,
	CalendarClock,
	CalendarDays,
	CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import getInitials from '@/utils/getInitials';
import getAvatarColor from '@/utils/getAvatarColor';
import {
	singleOffset,
	type Notifications,
} from '@/components/notifications/notificationSettings';
import type {
	Reminder,
	ReminderPriority,
	RecurrenceRule,
	NotificationOffset,
} from '@/slices/remindersApi';

export type ReminderBucket = 'overdue' | 'today' | 'upcoming' | 'completed';

export interface CategorizedReminders {
	overdue: Reminder[];
	today: Reminder[];
	upcoming: Reminder[];
	completed: Reminder[];
}

const startOfToday = (): Date => {
	const d = new Date();
	d.setHours(0, 0, 0, 0);
	return d;
};

const endOfToday = (): Date => {
	const d = new Date();
	d.setHours(23, 59, 59, 999);
	return d;
};

export const categorizeReminders = (
	reminders: Reminder[]
): CategorizedReminders => {
	const todayStart = startOfToday();
	const todayEnd = endOfToday();

	const result: CategorizedReminders = {
		overdue: [],
		today: [],
		upcoming: [],
		completed: [],
	};

	for (const reminder of reminders) {
		if (reminder.isCompleted) {
			result.completed.push(reminder);
			continue;
		}
		const due = new Date(reminder.dateTime);
		if (due < todayStart) result.overdue.push(reminder);
		else if (due <= todayEnd) result.today.push(reminder);
		else result.upcoming.push(reminder);
	}

	return result;
};

export const formatDueDate = (dateString: string): string => {
	const date = new Date(dateString);
	const now = new Date();
	const dateOnly = new Date(
		date.getFullYear(),
		date.getMonth(),
		date.getDate()
	);
	const todayOnly = new Date(
		now.getFullYear(),
		now.getMonth(),
		now.getDate()
	);
	const diffDays = Math.round(
		(dateOnly.getTime() - todayOnly.getTime()) / 86400000
	);

	const time = date.toLocaleTimeString([], {
		hour: 'numeric',
		minute: '2-digit',
	});

	if (diffDays === 0) return `Today, ${time}`;
	if (diffDays === 1) return `Tomorrow, ${time}`;
	if (diffDays === -1) return `Yesterday, ${time}`;
	if (diffDays > 1 && diffDays <= 7) {
		return `${date.toLocaleDateString([], { weekday: 'short' })}, ${time}`;
	}
	if (diffDays < -1 && diffDays >= -7) {
		return `${Math.abs(diffDays)} days ago`;
	}
	return `${date.toLocaleDateString([], {
		month: 'short',
		day: 'numeric',
	})}, ${time}`;
};

// Use the account's Moment locale for dashboard dates, including day-first dates.
export const formatDashboardDueDate = (dateString: string): string =>
	moment(dateString).calendar(null, {
		sameDay: '[Today] · LT',
		nextDay: '[Tomorrow] · LT',
		lastDay: '[Yesterday] · LT',
		nextWeek: 'll · LT',
		lastWeek: 'll · LT',
		sameElse: 'll · LT',
	});

export const PRIORITY_CONFIG: Record<
	ReminderPriority,
	{ label: string; color: string; dot: string }
> = {
	none: { label: 'None', color: 'text-muted-foreground', dot: 'bg-muted' },
	low: { label: 'Low', color: 'text-sky-600', dot: 'bg-sky-500' },
	med: { label: 'Medium', color: 'text-amber-600', dot: 'bg-amber-500' },
	high: { label: 'High', color: 'text-red-600', dot: 'bg-red-500' },
};

export const PRIORITY_OPTIONS: ReminderPriority[] = [
	'none',
	'low',
	'med',
	'high',
];

export const RECURRENCE_OPTIONS: { value: RecurrenceRule; label: string }[] = [
	{ value: 'none', label: 'Does not repeat' },
	{ value: 'daily', label: 'Daily' },
	{ value: 'weekly', label: 'Weekly' },
	{ value: 'monthly', label: 'Monthly' },
];

export const RECURRENCE_LABELS: Record<RecurrenceRule, string> = {
	none: '',
	daily: 'Daily',
	weekly: 'Weekly',
	monthly: 'Monthly',
};

// Stored as strings; 'none' maps to the null offset.
// Explicit checks only — `?? 0` would coerce null ("no notification") to 0
// and silently re-enable notifications. undefined (legacy docs) ≙ 0.
export const offsetToSelectValue = (
	offset: number | null | undefined
): string => {
	if (offset === null) return 'none';
	const normalized = offset === undefined ? 0 : offset;
	return String(normalized);
};

export const selectValueToOffset = (value: string): NotificationOffset => {
	if (value === 'none') return null;
	if (value === '5') return 5;
	if (value === '15') return 15;
	if (value === '30') return 30;
	if (value === '60') return 60;
	return 0;
};

// Sticky default: the last notification timing the user actively picked
// becomes the default for new reminders, remembered per device.
const NOTIFICATION_OFFSET_DEFAULT_KEY = 'reminderNotificationOffsetDefault';

export const getDefaultNotificationOffset = (): NotificationOffset => {
	try {
		const stored = window.localStorage.getItem(
			NOTIFICATION_OFFSET_DEFAULT_KEY
		);
		// Invalid/absent values fall through to 0 in selectValueToOffset.
		return stored === null ? 0 : selectValueToOffset(stored);
	} catch {
		return 0;
	}
};

export const storeDefaultNotificationOffset = (
	offset: number | null
): void => {
	try {
		window.localStorage.setItem(
			NOTIFICATION_OFFSET_DEFAULT_KEY,
			offsetToSelectValue(offset)
		);
	} catch {
		// localStorage can throw (private mode) — sticky default is best-effort.
	}
};

// Several times can't be one default, so only a single time or None is kept.
// Comparing with the starting point means an untouched form changes nothing.
export const keepNotificationDefault = (
	notifications: Notifications,
	startingOffset: number | null | undefined
): void => {
	const offset = singleOffset(notifications.offsets);
	if (offset !== undefined && offset !== startingOffset) {
		storeDefaultNotificationOffset(offset);
	}
};

// Snooze presets return an ISO date string relative to "now".
export const SNOOZE_PRESETS: { label: string; getDate: () => string }[] = [
	{
		label: 'In 1 hour',
		getDate: () => {
			const d = new Date();
			d.setHours(d.getHours() + 1);
			return d.toISOString();
		},
	},
	{
		label: 'In 3 hours',
		getDate: () => {
			const d = new Date();
			d.setHours(d.getHours() + 3);
			return d.toISOString();
		},
	},
	{
		label: 'Tomorrow morning',
		getDate: () => {
			const d = new Date();
			d.setDate(d.getDate() + 1);
			d.setHours(9, 0, 0, 0);
			return d.toISOString();
		},
	},
	{
		label: 'Next week',
		getDate: () => {
			const d = new Date();
			d.setDate(d.getDate() + 7);
			d.setHours(9, 0, 0, 0);
			return d.toISOString();
		},
	},
];

export const BUCKET_META: Record<
	ReminderBucket,
	{ label: string; icon: typeof Flag; iconClass: string }
> = {
	overdue: {
		label: 'Overdue',
		icon: AlertTriangle,
		iconClass: 'text-destructive',
	},
	today: { label: 'Today', icon: CalendarClock, iconClass: 'text-primary' },
	upcoming: {
		label: 'Upcoming',
		icon: CalendarDays,
		iconClass: 'text-primary/70',
	},
	completed: {
		label: 'Completed',
		icon: CheckCircle2,
		iconClass: 'text-muted-foreground',
	},
};

export const PriorityFlag = ({
	priority,
	className,
}: {
	priority: ReminderPriority;
	className?: string;
}) => {
	if (!priority || priority === 'none') return null;
	const config = PRIORITY_CONFIG[priority];
	return (
		<Flag
			className={cn('h-3.5 w-3.5 fill-current', config.color, className)}
			aria-label={`${config.label} priority`}
		/>
	);
};

export const RecurrenceBadge = ({ rule }: { rule?: RecurrenceRule }) => {
	if (!rule || rule === 'none') return null;
	return (
		<span className="inline-flex items-center text-xs text-muted-foreground">
			<Repeat className="h-3 w-3 mr-1" />
			{RECURRENCE_LABELS[rule]}
		</span>
	);
};

/** Most people a reminder can be assigned to (the server's Reminder.MAX_ASSIGNEES) */
export const MAX_ASSIGNEES = 20;

/** Reminders saved before the list existed only have `_assignee` */
export const getReminderAssignees = (reminder: Reminder) =>
	reminder._assignees?.length
		? reminder._assignees
		: reminder._assignee
			? [reminder._assignee]
			: [];

export const AssigneeAvatar = ({
	name,
	label,
	className,
}: {
	name?: string;
	/** Full display name, e.g. "Emily (emily@chekkit.io)" for duplicate names; shown on hover */
	label?: string;
	className?: string;
}) => {
	const displayName = name || 'Unassigned';
	const fullName = label || displayName;
	return (
		<Avatar className={cn('h-5 w-5', className)} title={fullName}>
			<AvatarFallback
				className="text-[9px] font-medium text-white"
				style={{ backgroundColor: getAvatarColor(fullName) }}
			>
				{getInitials(displayName)}
			</AvatarFallback>
		</Avatar>
	);
};
